import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Car, Shield, AlertTriangle, Info } from 'lucide-react';

export default function AutoTermsPage() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <Car className="w-5 h-5 text-[#1E3A5F]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">WANTD Auto — Terms & Conditions</h1>
            <p className="text-sm text-[var(--theme-text-muted)]">
              Effective date: April 2026 · Supplements the{' '}
              <Link to="/terms" className="text-[#1E3A5F] underline">General Terms of Service</Link>
            </p>
          </div>
        </div>

        <div className="space-y-8 text-[var(--theme-text-secondary)] text-sm leading-relaxed">

          {/* 1. Classifieds-only */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">1.</span> WANTD Auto Is a Classified Advertising Service
            </h2>
            <p>
              WANTD Pty Ltd (<strong>"WANTD"</strong>, <strong>"we"</strong>, <strong>"us"</strong>) operates WANTD Auto
              (<strong>"the Auto Section"</strong>) as an online <strong>classified advertising service</strong>.
              WANTD publishes listings for private sellers and (where available) licensed motor dealers. WANTD is
              <strong> not a party to any sale, transfer, or other transaction</strong> involving any vehicle listed
              on the Auto Section.
            </p>
            <p className="mt-2">
              WANTD does not take title to, possess, inspect, deliver, or warrant any vehicle. All negotiations,
              payments, inspections, and transfers occur directly between the seller and the buyer. WANTD receives
              revenue only from listing fees (and, where applicable, dealer subscriptions) — <strong>not</strong>
              {' '}from commissions on vehicle sales.
            </p>
            <div className="mt-3 p-3 rounded-lg bg-[#1E3A5F]/5 border border-[#1E3A5F]/20">
              <p className="text-xs text-[var(--theme-text-muted)]">
                <strong>ACL notice:</strong> Consumer guarantees under the Australian Consumer Law apply to vehicles
                sold by businesses (including licensed motor dealers) but generally do not apply to one-off private
                sales. Buyers should conduct their own due diligence on any vehicle listed as a private sale.
              </p>
            </div>
          </section>

          {/* 2. PPSR disclaimer */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">2.</span> PPSR Checks — Informational Only
            </h2>
            <p>
              Listings purchased as <strong>Plus</strong> may include a search of the Personal Property
              Securities Register (<strong>PPSR</strong>) performed through an approved third-party data provider.
              PPSR checks indicate matters registered against a VIN or vehicle identifier at the time of the search
              and may report on registered security interests, write-off status, and stolen-vehicle status.
            </p>
            <p className="mt-2">
              A PPSR check is provided for <strong>informational purposes only</strong>. WANTD makes no warranty
              that a PPSR result is accurate, current, complete, or fit for any particular purpose. WANTD does not
              guarantee vehicle title, absence of encumbrances, odometer accuracy, accident history, or physical
              condition. Buyers should obtain an independent mechanical inspection and their own PPSR search before
              purchasing any vehicle.
            </p>
            <p className="mt-2">
              At launch, PPSR integration may be <strong>pending activation</strong>. Listings purchased as
              Plus during the pending period will have the bundled PPSR check applied automatically once
              the service is activated, at no additional cost to the seller.
            </p>
          </section>

          {/* 3. WOVR disclaimer */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">3.</span> Written-Off Vehicles Register (WOVR) Disclosure
            </h2>
            <p>
              Sellers are required by state law to disclose if a vehicle has ever been recorded on the Written-Off
              Vehicles Register (WOVR) as a <strong>statutory</strong> or <strong>repairable</strong> write-off.
              WANTD requires sellers to select their WOVR status in the listing wizard.
            </p>
            <p className="mt-2">
              WANTD does not independently verify WOVR status and bears no responsibility for inaccurate or missing
              disclosures. Failure to disclose a write-off is a criminal offence in most Australian states and may
              expose the seller to civil and criminal liability. Buyers should verify WOVR status independently
              through their state road authority or a PPSR search.
            </p>
          </section>

          {/* 4. Takata recall */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">4.</span> Safety Recalls — Takata Airbags and Others
            </h2>
            <div className="flex items-start gap-2 p-3 rounded-lg border border-[#D97706]/30 bg-[#D97706]/5 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 flex-shrink-0" />
              <p className="text-xs">
                The compulsory Takata airbag recall affected millions of Australian vehicles. "Alpha" inflators
                are classified as extremely hazardous. Sellers must not misrepresent the safety recall status
                of any listed vehicle.
              </p>
            </div>
            <p>
              Sellers warrant they have disclosed any active manufacturer safety recall that they are aware of,
              including any outstanding Takata airbag recall. Buyers are responsible for checking the recall status
              of any vehicle before purchase. The Australian recall register is available at{' '}
              <a href="https://www.productsafety.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#1E3A5F] underline">
                productsafety.gov.au
              </a>
              {' '}and Takata-specific status can be checked at{' '}
              <a href="https://www.ismyairbagsafe.com.au" target="_blank" rel="noopener noreferrer" className="text-[#1E3A5F] underline">
                ismyairbagsafe.com.au
              </a>
              .
            </p>
          </section>

          {/* 5. Roadworthy / RWC */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">5.</span> Roadworthy & Safety Certificates
            </h2>
            <p>
              Roadworthy Certificate (RWC) and safety certificate requirements vary by state and by vehicle type.
              In Victoria, a Certificate of Roadworthiness is required before a registered vehicle can be
              transferred. In Queensland, a Safety Certificate is required for most light vehicles. Other states
              have their own rules.
            </p>
            <p className="mt-2">
              WANTD does not require sellers to provide a roadworthy or safety certificate before listing. Buyers
              are responsible for verifying the current roadworthiness requirements that apply to the transfer in
              their state and for obtaining any required inspection.
            </p>
          </section>

          {/* 6. Motor dealer licensing */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">6.</span> Motor Dealer Licensing
            </h2>
            <p>
              State laws require a person who carries on the business of buying, selling, or exchanging motor
              vehicles to hold a <strong>motor dealer licence</strong>. In most states, selling or advertising
              more than a small number of vehicles in any 12-month period may constitute "carrying on a business"
              and require a licence.
            </p>
            <p className="mt-2">
              WANTD prompts private sellers who post multiple vehicle listings in a 12-month period but does not
              enforce licensing requirements. You are responsible for determining whether a motor dealer licence
              is required for your activity. Licensed dealers may contact WANTD about Dealer-tier listings.
            </p>
          </section>

          {/* 7. No pay-through-WANTD */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">7.</span> Payments Are Direct — WANTD Does Not Process Vehicle Payments
            </h2>
            <div className="flex items-start gap-2 p-3 rounded-lg border border-[#1E3A5F]/20 bg-[#1E3A5F]/5 mb-3">
              <Info className="w-4 h-4 text-[#1E3A5F] mt-0.5 flex-shrink-0" />
              <p className="text-xs">
                WANTD Auto does <strong>not</strong> offer escrow, pay-through, or payment routing for vehicle
                sales. Any message suggesting you pay WANTD for a vehicle is fraudulent. Report it immediately
                to <a href="mailto:legal@wantd.au" className="text-[#1E3A5F] underline">legal@wantd.au</a>.
              </p>
            </div>
            <p>
              All payment for vehicles listed on the Auto Section is made directly between the buyer and seller.
              WANTD does not provide, operate, or broker any escrow service, financial service, or credit facility
              in connection with vehicle sales. Buyers should conduct their own due diligence — inspect the
              vehicle in person, verify identity, confirm ownership, and use safe payment methods (bank transfer
              in person, or bank cheque verified with the issuing bank) before releasing funds.
            </p>
            <p className="mt-2">
              WANTD is not a credit provider, not an authorised deposit-taking institution, and does not hold an
              Australian Financial Services Licence in connection with vehicle transactions. The $20 Plus
              listing fee charged by WANTD is a platform-service fee paid by the seller for listing and ancillary
              services (such as the bundled PPSR check); it is not a payment towards any vehicle.
            </p>
          </section>

          {/* 8. Refund policy */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">8.</span> Plus Listing Fee — Refund Policy
            </h2>
            <p>
              The Plus listing fee is <strong>AUD $20</strong>, charged once per listing and paid at the
              time of listing submission. The fee covers publication of the listing as Plus, the bundled
              PPSR check (once available), extended listing features, and indefinite listing duration.
            </p>
            <p className="mt-2">
              <strong>Refund window:</strong> Sellers may request a full refund within <strong>1 hour</strong> of
              payment, provided the listing has not yet been published publicly. Once a listing is publicly
              visible on the Auto Section, the fee is non-refundable. Refund requests must be made through the
              listing management page or by contacting{' '}
              <a href="mailto:support@wantd.au" className="text-[#1E3A5F] underline">support@wantd.au</a>.
            </p>
            <p className="mt-2">
              Nothing in this refund policy excludes any non-excludable right that you may have under the
              Australian Consumer Law.
            </p>
          </section>

          {/* 9. Prohibited listings */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">9.</span> Prohibited Listings
            </h2>
            <p className="mb-3">The following listings are prohibited and will be removed without notice:</p>
            <div className="space-y-1.5">
              {[
                'Vehicles that are the subject of an active theft report, without clear title',
                'Vehicles with tampered or removed VIN or engine numbers',
                'Vehicles being sold while subject to an undisclosed finance encumbrance',
                'Listings that conceal a statutory write-off or active manufacturer safety recall',
                'Listings that use stock manufacturer or dealer-brochure imagery without disclosing the listing is not for a specific physical vehicle',
                'Listings by unlicensed commercial dealers attempting to trade as private sellers',
                'Listings offering to ship or freight a vehicle interstate as part of the sale, without in-person inspection availability',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 10. WANTD rights */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">10.</span> WANTD's Rights and Platform Moderation
            </h2>
            <p>
              WANTD reserves the right to remove any listing from the Auto Section at any time, with or without
              notice, if WANTD reasonably believes the listing violates these terms, does not comply with
              applicable law, misrepresents the vehicle, or is otherwise harmful to users or to the reputation
              of the platform. No refund of the Plus listing fee is payable where a listing is removed
              for breach of these terms.
            </p>
          </section>

          {/* 11. Limitation of liability */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">11.</span> Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by Australian law, WANTD's liability in connection with the Auto
              Section is limited to the greater of the listing fees paid by you (if any) or AUD $100. WANTD is
              not liable for any loss, damage, injury, or expense arising from a transaction facilitated through
              the Auto Section, from any inaccuracy in a listing, or from any PPSR, WOVR, or other data provided
              through a third-party information service. This limitation does not exclude liability for death or
              personal injury caused by WANTD's negligence, or any other liability that cannot be excluded by law.
            </p>
          </section>

          {/* 12. Governing law */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">12.</span> Governing Law
            </h2>
            <p>
              These Auto Terms are governed by the laws of Victoria, Australia. The parties submit to the
              non-exclusive jurisdiction of the courts of Victoria. These terms supplement and do not replace
              WANTD's{' '}
              <Link to="/terms" className="text-[#1E3A5F] underline">General Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#1E3A5F] underline">Privacy Policy</Link>
              . In the event of a conflict, these Auto Terms prevail to the extent of the inconsistency for
              matters relating to the Auto Section.
            </p>
          </section>

          {/* 13. Contact */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">13.</span> Contact
            </h2>
            <p>
              Questions about these terms or to report suspicious listings:{' '}
              <a href="mailto:legal@wantd.au" className="text-[#1E3A5F] underline">legal@wantd.au</a>
            </p>
          </section>

          {/* Footer */}
          <div className="flex items-center gap-2 pt-4 border-t border-[var(--theme-border)]">
            <Shield className="w-4 h-4 text-[#059669]" />
            <p className="text-xs text-[var(--theme-text-muted)]">
              WANTD Auto is a classifieds service — WANTD is not a party to any vehicle transaction. Do your own
              due diligence before buying or selling a vehicle.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
